<template>
  <!-- Original dialog kept but hidden when our custom overlay is active -->
  <v-dialog v-if="!useCustomOverlay" v-model="dialog" max-width="480px" class="timeout-modal delete-popup">
    <v-card class="timeout-modal-card">
      <v-card-title class="headline">{{ title }}</v-card-title>
      <v-card-text>
        {{ baseMessage }}
        <div v-if="countdown > 0" class="countdown-text">{{ $t('message.closesIn', { seconds: countdown }) }}</div>
      </v-card-text>
      <v-card-actions>
        <v-btn class="text-cancel-btn" variant="outlined" @click="cancel">{{ options.cancelText || $t('message.cancel') }}</v-btn>
        <v-spacer></v-spacer>
        <v-btn class="text-delete-btn" variant="elevated" @click="agree">{{ options.confirmText || $t('message.submit') }}</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>

  <!-- Custom overlay that exactly matches the global-loading-overlay -->
  <div v-if="useCustomOverlay && dialog" class="global-loading-overlay custom-modal-overlay">
    <div class="custom-modal-container">
      <v-card class="timeout-modal-card delete-popup">
        <v-card-title class="headline">{{ title }}</v-card-title>
        <v-card-text>
          {{ baseMessage }}
          <div v-if="countdown > 0" class="countdown-text">{{ $t('message.closesIn', { seconds: countdown }) }}</div>
        </v-card-text>
        <v-card-actions>
          <v-btn class="text-cancel-btn" variant="outlined" @click="cancel">{{ options.cancelText || $t('message.cancel') }}</v-btn>
          <v-spacer></v-spacer>
          <v-btn class="text-delete-btn" variant="elevated" @click="agree">{{ options.confirmText || $t('message.submit') }}</v-btn>
        </v-card-actions>
      </v-card>
    </div>
  </div>
</template>


<script>

export default {
  name: 'ConfirmModal',
  data: () => ({
    dialog: false,
    resolve: null,
    reject: null,
    baseMessage: null,
    title: null,
    useCustomOverlay: true, // Set to true to use our custom overlay instead of v-dialog
    options: {
      width: 480,
      zIndex: 200,
      confirmText: 'Submit',
      cancelText: 'Cancel',
      initialCountdown: 0,
    },
    countdown: 0,
    countdownInterval: null,
  }),
  computed: {
    isActive() {
      return this.dialog;
    }
  },
  methods: {
    open(title, message, options) {
      this.dialog = true;
      this.title = title;
      this.baseMessage = message;
      this.options = Object.assign({}, this.options, options);

      if (this.options && this.options.initialCountdown > 0) {
        this.startCountdown(this.options.initialCountdown);
      }
      
      return new Promise((resolve, reject) => {
        this.resolve = resolve;
        this.reject = reject;
      });
    },
    startCountdown(initialValue) {
      this.countdown = initialValue;
      this.clearCountdownInternal();
      
      this.countdownInterval = setInterval(() => {
        if (this.countdown > 0) {
          this.countdown--;
        } else {
          this.clearCountdownInternal();
          // Automatically trigger cancel when countdown reaches zero
          if (this.dialog) {
            this.cancel();
          }
        }
      }, 1000);
    },
    clearCountdownInternal() {
      if (this.countdownInterval) {
        clearInterval(this.countdownInterval);
        this.countdownInterval = null;
      }
    },
    agree() {
      this.clearCountdownInternal();
      if (typeof this.resolve === 'function') {
        this.resolve(true);
      } 
      // Silent fail when this.resolve is not a function
      this.dialog = false;
      this.reset();
    },
    cancel() {
      this.clearCountdownInternal();
      if (typeof this.resolve === 'function') {
        this.resolve(false);
      } 
      // Silent fail when this.resolve is not a function
      this.dialog = false;
      this.reset();
    },
    close() {
      this.clearCountdownInternal();
      this.dialog = false;
      this.reset();
    },
    reset() {
      this.title = null;
      this.baseMessage = null;
      this.resolve = null;
      this.reject = null;
      this.options = { width: 480, zIndex: 200, confirmText: 'Submit', cancelText: 'Cancel', initialCountdown: 0 };
      this.countdown = 0;
    }
  },
  beforeUnmount() {
    this.clearCountdownInternal();
  }
};
</script>

<style lang="scss">
@use '@/styles/_index' as *;


/* Add global styles to override Vuetify's dialog for our timeout modal */
.v-dialog.timeout-modal {
  .v-overlay__scrim {
    background-color: rgba(255, 255, 255, 0.9) !important;
    opacity: 1 !important;
  }
}

/* Force the background to be white */
.v-overlay--active {
  &.timeout-modal {
    background-color: rgba(255, 255, 255, 0.9) !important;
  }
}

/* Apply global styles */
:root {
  --timeout-modal-bg: rgba(255, 255, 255, 0.9);
}

.timeout-modal-card {
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1) !important;
  border: 1px solid rgba(0, 0, 0, 0.05);
}

/* Global override for all dialog backdrops - use with caution */
.v-overlay__scrim {
  .timeout-modal & {
    background-color: var(--timeout-modal-bg) !important;
    opacity: 1 !important;
  }
}

/* Reuse the same styles as global-loading-overlay from App.vue */
.custom-modal-overlay {
  /* No need to override these as we're directly reusing the global-loading-overlay class */
  
  /* Add styles for the modal container inside the overlay */
  .custom-modal-container {
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
    height: 100%;
  }
  
  /* Style the modal card */
  .timeout-modal-card {
    max-width: 480px;
    width: 100%;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1) !important;
    border: 1px solid rgba(0, 0, 0, 0.05);
  }
}

/* Additional styles for buttons inside the custom overlay */
.custom-modal-overlay .v-btn {
  margin: 0 4px;
}

/* Admin panel dialog styles */
.delete-popup .headline {
  padding: 4px 16px;
  height: 64px !important;
  align-items: center;
  display: flex;
}

.delete-popup .v-card-text {
  padding: 20px !important;
  color: #00000099;
  font-size: 14px !important;
  font-weight: 400;
}

.delete-popup .v-card-actions {
  justify-content: space-between !important;
  padding: 20px !important;
  margin-top: 20px !important;
}

.delete-popup .v-card-actions .text-cancel-btn {
  color: #000000de;
  width: 152px;
  height: 32px;
  border-radius: 3px;
  border: 1px solid #cdcdcd;
  font-size: 14px;
  font-weight: 500 !important;
}

.delete-popup .v-card-actions .text-delete-btn {
  border: 1px solid #64e2b5;
  color: #fff;
  background: #64e2b5;
  width: 152px;
  height: 32px;
  font-size: 14px;
  font-weight: 500;
}

/* Countdown text styling */
.countdown-text {
  margin-top: 10px;
  font-size: 14px;
  color: #757575;
  font-style: italic;
}
</style>
